/* eslint-disable react-hooks/exhaustive-deps */
import {
  DataGrid,
  type ColumnDef as DataGridColumnDef,
} from '@tonic-one/react-data-grid';
import { Box, useColorMode } from '@tonic-one/react';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PolicyActionCell,
  PolicyNameCell,
  PolicyStatusSwitch,
  PolicyManualScanCell,
  PolicyRulesCell,
} from '../../TableCell';
import {
  PolicyItemInTable,
  PolicyListModalState,
  OverAllPolicyType,
  ServiceCollection,
} from '../../utils/types';
import { PolicyManualScanRunningQueue } from '../context/OverAllPolicyTypeContext';
import { PolicyTypeEnum } from '../../utils/constants';
import { getPolicyName } from '../../utils/fns';
import { useLicenseStore } from '@Models/useLicenseModel';
import {
  usePolicyMutation,
  createPolicyMutationKey,
} from '../context/PolicyMutationContext';
import { useOrgStore } from '@Models/useOrgModel';

function PolicyListTable({
  policyType,
  setModal,
  data,
  setRefreshTableTrigger,
  policyManualScanRunningQueue,
  overAllPolicyType,
  collectionType,
}: {
  policyType: PolicyTypeEnum;
  setModal: ({ name, policy }: PolicyListModalState) => void;
  data: PolicyItemInTable[];
  setRefreshTableTrigger: (trigger: Date) => void;
  policyManualScanRunningQueue?: PolicyManualScanRunningQueue;
  overAllPolicyType: OverAllPolicyType;
  collectionType: ServiceCollection;
}) {
  const [colorMode] = useColorMode();
  const { t } = useTranslation();
  const { mutatingPolicyTypes, reorderingPolicyTypes, reorderMutation } =
    usePolicyMutation();
  const { orgId } = useOrgStore();
  const hoverBackgroundColor = {
    dark: 'rgba(255, 255, 255, 0.12)',
    light: 'rgba(0, 0, 0, 0.12)',
  }[colorMode];

  // Get the policy type name using the first policy item
  const policyTypeName =
    data.length > 0
      ? getPolicyName(data[0].policyType, data[0].policySubType)
      : null;

  // Create the mutation key for this specific policy table
  const mutationKey =
    data.length > 0
      ? createPolicyMutationKey(
          data[0].policyType,
          data[0].policySubType,
          overAllPolicyType,
          collectionType,
          orgId
        )
      : null;

  const { hasEmailAddOnLicense } = useLicenseStore();

  // In some scenarios, need to disable all actions
  // For now just for inline policy when add-on license disable
  const disableAll =
    !hasEmailAddOnLicense &&
    (policyType === PolicyTypeEnum.ExchangeInline ||
      policyType === PolicyTypeEnum.GmailInline);

  // Sorting state to track if any column is sorted
  const [sorting, setSorting] = useState({});

  const cellCenteringProps = {
    display: 'flex',
    alignItems: 'center',
  };

  const columns: DataGridColumnDef[] = useMemo(
    () => [
      {
        id: 'priority',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('policy.table.fields.priority')}
          </TextEllipsis>
        ),
        accessorKey: 'policyPriority',
        size: 'auto',
        width: '6%',
        cell: ({ cell }: any) => (
          <TextEllipsis
            popperProps={{ usePortal: true }}
            contentProps={{ width: '100%', textAlign: 'end', pr: '2x' }}
          >
            {cell.value}
          </TextEllipsis>
        ),
        cellProps: cellCenteringProps,
      },
      {
        id: 'status',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('policy.table.fields.status')}
          </TextEllipsis>
        ),
        size: 'auto',
        accessorKey: 'policyRealtimeEnabled',
        cell: ({ row }: any) => {
          return (
            <PolicyStatusSwitch
              policy={row.value}
              setModal={setModal}
              disabled={disableAll}
              collectionType={collectionType}
            />
          );
        },
        width: '5%',
        cellProps: cellCenteringProps,
      },
      {
        id: 'policyName',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('policy.table.fields.policyName')}
          </TextEllipsis>
        ),
        accessorKey: 'policyName',
        sortingFn: (a: any, b: any) => {
          const nameA = a.row.value.policyName;
          const nameB = b.row.value.policyName;
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        },
        cell: ({ row }: any) => {
          return <PolicyNameCell policy={row.value} disabled={disableAll} />;
        },
        width: '26%',
        cellProps: cellCenteringProps,
      },
      {
        id: 'policyTarget',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('policy.table.fields.policyTarget')}
          </TextEllipsis>
        ),
        accessorKey: 'policyTarget',
        sortingFn: (a: any, b: any) => {
          const targetA = a.row.value.policyTarget;
          const targetB = b.row.value.policyTarget;
          return targetA.localeCompare(targetB, undefined, {
            sensitivity: 'base',
          });
        },
        width: '20%',
        cell: ({ cell }: any) => (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {cell.value}
          </TextEllipsis>
        ),
        cellProps: cellCenteringProps,
      },
      {
        id: 'policyRule',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('policy.table.fields.policyRule')}
          </TextEllipsis>
        ),
        isSortable: false,
        cell: ({ row }: any) => {
          return (
            <TextEllipsis popperProps={{ usePortal: true }}>
              <PolicyRulesCell
                policy={row.value}
                type={overAllPolicyType}
                isFirstRow={row.index === 0}
              />
            </TextEllipsis>
          );
        },
        width: '20%',
        minWidth: 280,
        cellProps: cellCenteringProps,
      },
      {
        id: 'manualScan',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('policy.table.fields.manualScan')}
          </TextEllipsis>
        ),
        accessorKey: 'scanHistory',
        cell: ({ row }: any) => {
          return (
            <TextEllipsis popperProps={{ usePortal: true }}>
              <PolicyManualScanCell policy={row.value} setModal={setModal} />
            </TextEllipsis>
          );
        },
        width: '12%',
        cellProps: cellCenteringProps,
      },
      {
        id: 'actions',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('policy.table.fields.actions')}
          </TextEllipsis>
        ),
        size: 'auto',
        isSortable: false,
        cell: ({ row }: any) => {
          return (
            <PolicyActionCell
              policy={row.value}
              setModal={setModal}
              setRefreshTableTrigger={setRefreshTableTrigger}
              policyManualScanRunningQueue={policyManualScanRunningQueue}
              disabled={disableAll}
            />
          );
        },
        width: '5%',
        cellProps: cellCenteringProps,
      },
    ],
    [
      disableAll,
      setModal,
      setRefreshTableTrigger,
      policyManualScanRunningQueue,
      overAllPolicyType,
      t,
    ]
  );

  // Row reordering handler
  const handleRowOrderChange = (_newData: PolicyItemInTable[], info: any) => {
    if (!policyTypeName) return;

    const { oldIndex, targetIndex } = info;
    const draggedPolicy = data[oldIndex];

    // Calculate the new priority based on target position
    let newPriority: number;

    if (targetIndex >= data.length) {
      // Dragged to the end - place after the last item
      const lastPolicy = data[data.length - 1];
      newPriority = lastPolicy.policyPriority;
    } else {
      // Dragged to a specific position - take the target policy's priority
      const targetPolicy = data[targetIndex];
      newPriority = targetPolicy.policyPriority;
    }

    // Execute mutation immediately - onMutate handles the optimistic update
    reorderMutation.mutate({
      policyId: draggedPolicy.policyUuid,
      newPriority,
      oldIndex,
      targetIndex,
    });
  };

  // Determine if a row can be reordered (non-global policies only and no active sorting)
  const isRowReorderable = ({ row }: { row: { value: PolicyItemInTable } }) => {
    return row.value.policyIsglobal !== 1 && !Object.keys(sorting).length;
  };

  // Get unique row ID
  const getRowId = (row: PolicyItemInTable) => row.policyUuid;

  // Calculate dynamic minimum height based on number of rows
  const rowHeight = 44;
  const headerHeight = 38;
  const visibleRows = Math.min(data.length, 5); // Cap at 5 rows for minHeight calculation
  const dynamicMinHeight = headerHeight + visibleRows * rowHeight;

  return (
    <Box
      width="100%"
      minHeight={dynamicMinHeight}
      overflow="auto"
      sx={{
        '--tonic-data-grid-row-hover-background-color': hoverBackgroundColor,
      }}
    >
      <DataGrid
        data={data}
        columns={columns}
        getRowId={getRowId}
        isRowReorderable={isRowReorderable}
        onRowOrderChange={handleRowOrderChange}
        isColumnResizable
        isColumnSortable
        autoSorting
        onSortingChange={setSorting}
        emptyContent={t('common.noData')}
        isLoading={
          mutationKey
            ? reorderingPolicyTypes.has(mutationKey) ||
              mutatingPolicyTypes.has(mutationKey)
            : false
        }
      />
    </Box>
  );
}

export default PolicyListTable;
